/**
 * OnboardingContext - Centralized state management for the onboarding flow
 * Manages all state related to data sources, statistics, and onboarding progress
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useMarketData } from '../hooks/useMarketData';
import { Client, ClientCreateData } from '../types/client';
import { clientService } from '../services/clientService';
import { httpClient } from '../lib/httpClient';

// Types
export type ScreenType = 'welcome' | 'data-hub' | 'quick-review' | 'completeness' | 'dashboard';

export interface DocumentStats {
  totalDocuments: number;
  approvedDocuments: number;
  pendingApproval: number;
  totalRows: number;
  avgProcessingTime: number;
  qualityScore: number;
}

export interface MarketStats {
  isConnected: boolean;
  lastQuoteTime: string | null;
  quotesThisHour: number;
  rateLimitRemaining: number;
}

export interface SourceCard {
  id: string;
  icon: string;
  title: string;
  description: string;
  supports: string;
  tooltip: string;
  bgColor: string;
  disabled?: boolean;
  comingSoon?: string;
}

export interface Upload {
  upload_id: string;
  filename: string;
  file_size: number;
  file_type: string;
  template_type: string;
  upload_status: string;
  total_rows: number;
  created_at: string;
  error_message?: string;
}

// Context interface
interface OnboardingContextType {
  // Screen navigation
  currentScreen: ScreenType;
  setCurrentScreen: (screen: ScreenType) => void;
  
  // Client management
  selectedClient: Client | null;
  setSelectedClient: (client: Client | null) => void;
  clients: Client[];
  setClients: (clients: Client[]) => void;
  isLoadingClients: boolean;
  clientError: string | null;
  fetchClients: () => Promise<void>;
  createClient: (data: ClientCreateData) => Promise<Client | null>;
  searchClients: (query: string) => Promise<void>;
  
  // Data sources
  selectedSources: string[];
  setSelectedSources: (sources: string[] | ((prev: string[]) => string[])) => void;
  connectedSources: string[];
  setConnectedSources: (sources: string[] | ((prev: string[]) => string[])) => void;
  
  // Statistics
  documentStats: DocumentStats;
  setDocumentStats: (stats: DocumentStats) => void;
  marketStats: MarketStats;
  setMarketStats: (stats: MarketStats) => void;
  
  // Uploads
  allUploads: Upload[];
  setAllUploads: (uploads: Upload[]) => void;
  
  // Actions
  fetchDocumentStatistics: () => Promise<void>;
  fetchMarketStatistics: () => Promise<void>;
  refreshStatistics: () => Promise<void>;
  refreshConnectedSources: () => Promise<void>;
  
  // Data sources configuration
  dataSources: SourceCard[];
}

// Create context
const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

// Data sources configuration
const dataSources: SourceCard[] = [
  {
    id: 'excel',
    icon: '📊',
    title: 'Excel / CSV Files',
    description: 'Drag holdings & trades here',
    supports: 'Supports: .xlsx, .csv, .xls',
    tooltip: 'Upload portfolio holdings, transactions, and account data from Excel or CSV files',
    bgColor: 'bg-kielis-success'
  },
  {
    id: 'email',
    icon: '📧',
    title: 'Email & Calendar',
    description: 'Connect Outlook or Gmail for meeting prep and client history',
    supports: 'Supports: Microsoft 365, Google Workspace',
    tooltip: 'Sync emails, calendar events, and meeting history for AI-powered client preparation',
    bgColor: 'bg-blue-500'
  },
  {
    id: 'pdf',
    icon: '📄',
    title: 'PDF Documents',
    description: 'Pull numbers from PDFs (statements, reports)',
    supports: 'Supports: Bank statements, fund reports',
    tooltip: 'Extract transaction data, balances, and performance metrics from PDF documents',
    bgColor: 'bg-red-500'
  },
  {
    id: 'custodian',
    icon: '🏦',
    title: 'Custodian API',
    description: 'Direct connection to your custodian bank',
    supports: 'Coming soon: Credit Suisse, UBS, Julius Baer',
    tooltip: '',
    bgColor: 'bg-gray-500',
    disabled: true,
    comingSoon: 'Q4 2025'
  },
  {
    id: 'market',
    icon: '📈',
    title: 'Market Data',
    description: 'Real-time pricing via Finnhub API',
    supports: 'Includes: US, EU, Asian markets + news',
    tooltip: 'Real-time stock quotes, company news, and financial data via Finnhub API',
    bgColor: 'bg-yellow-500'
  },
  {
    id: 'crm',
    icon: '👥',
    title: 'CRM Integration',
    description: 'Sync client data and meeting notes',
    supports: 'Supports: Salesforce, HubSpot, Pipedrive',
    tooltip: 'Two-way sync of client records, meeting notes, and relationship data with your CRM system',
    bgColor: 'bg-purple-500'
  }
];

// Provider component
export const OnboardingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Screen state
  const [currentScreen, setCurrentScreen] = useState<ScreenType>('welcome');
  
  // Client state
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [clientError, setClientError] = useState<string | null>(null);
  
  // Data sources state
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [connectedSources, setConnectedSources] = useState<string[]>([]);

  // Market data hook (same pattern as OAuth would be used in DataSourceWizard)
  const { isConnected: isMarketDataConnected } = useMarketData();
  
  // Statistics state
  const [documentStats, setDocumentStats] = useState<DocumentStats>({
    totalDocuments: 0,
    approvedDocuments: 0,
    pendingApproval: 0,
    totalRows: 0,
    avgProcessingTime: 0,
    qualityScore: 0
  });
  
  const [marketStats, setMarketStats] = useState<MarketStats>({
    isConnected: false,
    lastQuoteTime: null,
    quotesThisHour: 0,
    rateLimitRemaining: 60
  });
  
  // Uploads state
  const [allUploads, setAllUploads] = useState<Upload[]>([]);
  
  // Fetch clients from API
  const fetchClients = useCallback(async () => {
    setIsLoadingClients(true);
    setClientError(null);
    try {
      const response = await clientService.fetchClients(0, 100, { active: true });
      setClients(response.clients);
      
      // If we have a selected client ID but no selected client object, try to find it
      if (!selectedClient && response.clients.length > 0) {
        const storedClientId = localStorage.getItem('selectedClientId');
        if (storedClientId) {
          const client = response.clients.find(c => c.id === storedClientId);
          if (client) {
            setSelectedClient(client);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      setClientError('Failed to load clients. Please try again.');
      setClients([]);
    } finally {
      setIsLoadingClients(false);
    }
  }, [selectedClient]);

  // Create a new client
  const createClient = useCallback(async (data: ClientCreateData): Promise<Client | null> => {
    setClientError(null);
    try {
      // The canonical service throws on failure rather than returning {success:false};
      // the catch below already turns that into the same user-facing error.
      const client = await clientService.createClient(data);
      setClients(prev => [client, ...prev]);
      setSelectedClient(client);
      localStorage.setItem('selectedClientId', client.id);
      return client;
    } catch (error) {
      console.error('Failed to create client:', error);
      // The server says why it refused (duplicate email, invalid field). Replacing that with
      // "Please try again" tells the advisor to repeat the exact request that just failed.
      setClientError(
        error instanceof Error && error.message
          ? error.message
          : 'Failed to create client. Please try again.'
      );
      return null;
    }
  }, []);

  // Search clients
  const searchClients = useCallback(async (query: string) => {
    if (!query.trim()) {
      // If empty query, fetch all clients
      await fetchClients();
      return;
    }
    
    setIsLoadingClients(true);
    setClientError(null);
    try {
      setClients(await clientService.searchClients(query));
    } catch (error) {
      console.error('Failed to search clients:', error);
      setClientError('Failed to search clients. Please try again.');
    } finally {
      setIsLoadingClients(false);
    }
  }, [fetchClients]);

  // Persist selected client to localStorage
  useEffect(() => {
    if (selectedClient) {
      localStorage.setItem('selectedClientId', selectedClient.id);
    }
  }, [selectedClient]);

  // Load clients on mount
  useEffect(() => {
    fetchClients();
  }, []);

  // Fetch document statistics
  // TASK-083: Uses httpClient for gateway enforcement
  const fetchDocumentStatistics = useCallback(async () => {
    try {
      // Get client ID - use selected client or fall back to default
      const clientId = selectedClient?.id || 'default-client';

      // Fetch approval queue for pending documents via gateway
      const approvalQueue = await httpClient.get<any>(`/uploads/approval-queue?client_id=${clientId}`);

      // Fetch all uploads for total statistics via gateway
      const allUploadsData = await httpClient.get<any[]>(`/uploads/list?client_id=${clientId}`);

      const completedUploads = allUploadsData.filter((upload: any) => upload.upload_status === 'complete');
      const pendingUploads = approvalQueue.documents || [];

      // Calculate quality score based on error percentages
      const avgErrorPercentage = pendingUploads.length > 0
        ? pendingUploads.reduce((sum: number, doc: any) => sum + (doc.error_percentage || 0), 0) / pendingUploads.length
        : 0;
      const qualityScore = Math.max(0, Math.min(100, 100 - avgErrorPercentage));

      setDocumentStats({
        totalDocuments: allUploadsData.length,
        approvedDocuments: completedUploads.length,
        pendingApproval: pendingUploads.length,
        totalRows: allUploadsData.reduce((sum: number, upload: any) => sum + (upload.total_rows || 0), 0),
        avgProcessingTime: 2.3, // TODO: Calculate from real processing times
        qualityScore: qualityScore
      });

      setAllUploads(allUploadsData);
    } catch (error) {
      console.error('Error fetching document statistics:', error);
    }
  }, [selectedClient]);
  
  // Fetch market statistics
  // TASK-083: Uses httpClient for gateway enforcement
  const fetchMarketStatistics = useCallback(async () => {
    try {
      // Fetch market data via gateway
      const marketData = await httpClient.get<any>('/market/live-status');

      setMarketStats(prevStats => ({
        isConnected: marketData.status === 'connected' || prevStats.isConnected,
        lastQuoteTime: marketData.lastQuoteTime || prevStats.lastQuoteTime,
        quotesThisHour: marketData.quotesThisHour || prevStats.quotesThisHour,
        rateLimitRemaining: marketData.rateLimitRemaining || prevStats.rateLimitRemaining
      }));
    } catch (error) {
      console.error('Error fetching market statistics:', error);
      // If API fails, don't reset the connection status if it was previously connected
    }
  }, []);
  
  // Refresh all statistics
  const refreshStatistics = useCallback(async () => {
    await Promise.all([
      fetchDocumentStatistics(),
      fetchMarketStatistics()
    ]);
  }, [fetchDocumentStatistics, fetchMarketStatistics]);

  // Refresh connected sources by checking current status
  const refreshConnectedSources = useCallback(async () => {
    console.log('Refreshing connected sources...');
    const sources: string[] = [];

    try {
      // Check email/OAuth connection
      const authToken = localStorage.getItem('auth_token');
      if (authToken) {
        console.log('Email token found, adding email to sources');
        sources.push('email');
      }

      // Check market data connection using hook
      if (isMarketDataConnected) {
        console.log('Market data is connected via hook, adding to sources');
        sources.push('market');
      } else {
        console.log('Market data not connected via hook');
      }

      // Always update connected sources (even if empty array)
      console.log('Setting connected sources to:', sources);
      setConnectedSources(sources);
    } catch (error) {
      console.error('Error refreshing connected sources:', error);
    }
  }, [isMarketDataConnected]);

  // Initialize connected sources by checking existing configurations
  useEffect(() => {
    // Run initialization once when context loads
    refreshConnectedSources();
  }, [refreshConnectedSources]);
  
  // Set up polling for statistics when on review or platform ready screens
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    
    if (currentScreen === 'quick-review' || currentScreen === 'completeness') {
      // Initial fetch
      refreshStatistics();
      
      // Set up polling
      intervalId = setInterval(refreshStatistics, 5000); // Poll every 5 seconds
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [currentScreen, refreshStatistics]);
  
  const value: OnboardingContextType = {
    // Screen navigation
    currentScreen,
    setCurrentScreen,
    
    // Client management
    selectedClient,
    setSelectedClient,
    clients,
    setClients,
    isLoadingClients,
    clientError,
    fetchClients,
    createClient,
    searchClients,
    
    // Data sources
    selectedSources,
    setSelectedSources,
    connectedSources,
    setConnectedSources,
    
    // Statistics
    documentStats,
    setDocumentStats,
    marketStats,
    setMarketStats,
    
    // Uploads
    allUploads,
    setAllUploads,
    
    // Actions
    fetchDocumentStatistics,
    fetchMarketStatistics,
    refreshStatistics,
    refreshConnectedSources,
    
    // Data sources configuration
    dataSources
  };
  
  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

// Custom hook to use the context
export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};